import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { absoluteUrl } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Home", tid: "nav-home" },
  { to: "/menus", label: "Menus", tid: "nav-menus" },
  { to: "/location", label: "Location", tid: "nav-location" },
  { to: "/reservation", label: "Reservation", tid: "nav-reservation" },
  { to: "/contact", label: "Contact", tid: "nav-contact" },
];

const DEFAULT_LOGO_URL =
  "/nelum-logo-dark.png";

export const NelumMark = ({ className = "" }) => {
  const { settings } = useSettings();
  const tint = settings?.logo_tint !== false; // default true
  const logoUrl = settings?.logo_url
    ? (absoluteUrl(settings.logo_url) || DEFAULT_LOGO_URL)
    : DEFAULT_LOGO_URL;

  return (
    <Link
      to="/"
      data-testid="nav-brand"
      aria-label="NELUM — Modern Sri Lankan Cuisine"
      className={`inline-flex items-center ${className}`}
    >
      {tint ? (
        // CSS-mask logo: fills with the theme's primary colour.
        <span
          aria-hidden="true"
          data-testid="nav-logo-tinted"
          className="h-20 md:h-24 lg:h-28 w-[130px] md:w-[150px] lg:w-[176px] inline-block drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)]"
          style={{
            backgroundColor: "hsl(var(--primary))",
            WebkitMaskImage: `url(${logoUrl})`,
            maskImage: `url(${logoUrl})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
            WebkitMaskSize: "contain",
            maskSize: "contain",
          }}
        />
      ) : (
        // Static: original uploaded image rendered at its own colours.
        <img
          src={logoUrl}
          alt="Site logo"
          data-testid="nav-logo-static"
          className="h-20 md:h-24 lg:h-28 w-[130px] md:w-[150px] lg:w-[176px] object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)]"
        />
      )}
    </Link>
  );
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,border-color,backdrop-filter,box-shadow] duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-background/95 border-b border-primary/25 shadow-[0_10px_35px_rgba(0,0,0,0.35)]"
          : "bg-gradient-to-b from-background/95 via-background/75 to-background/45 backdrop-blur-md border-b border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.25)]"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 h-24 md:h-28 lg:h-32 flex items-center justify-between">
        <NelumMark />

        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.tid}
              className={({ isActive }) =>
                `link-underline text-[0.72rem] uppercase tracking-[0.22em] font-semibold drop-shadow-[0_1px_4px_rgba(0,0,0,0.95)] transition-colors duration-300 ${
                  isActive ? "text-primary" : "text-foreground hover:text-primary"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/reservation"
            data-testid="nav-cta-reserve"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-[0.72rem] uppercase tracking-[0.22em] font-semibold border border-primary bg-background/55 text-primary shadow-[0_2px_12px_rgba(0,0,0,0.45)] hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
          >
            Reserve
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-foreground p-2 border border-white/15 bg-background/55 shadow-[0_2px_10px_rgba(0,0,0,0.4)]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl"
        >
          <nav className="px-6 py-8 flex flex-col gap-6">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`${l.tid}-mobile`}
                className={({ isActive }) =>
                  `text-[0.78rem] uppercase tracking-[0.24em] transition-colors ${
                    isActive ? "text-primary" : "text-foreground/85"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/reservation"
              data-testid="nav-cta-reserve-mobile"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 text-[0.72rem] uppercase tracking-[0.22em] font-semibold border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              Reserve a Table
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
